import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, LayoutGrid, Table, AlertCircle, FileText, DollarSign, CheckCircle, Settings, TrendingUp, Plus } from 'lucide-react';
import { usePipelineDeals } from '../../hooks/usePipelineDeals';
import { usePipelineStagesConfig } from '../../hooks/usePipelineStagesConfig';
import { useTimezone } from '../../hooks/useTimezone';
import { useAuth } from '../../contexts/AuthContext';
import { canEditPipelineStages } from '../../utils/permissions';
import { formatInTz } from '../../utils/dateFormatters';
import LeadCard from './LeadCard';
import PipelineTable from './PipelineTable';

import { PipelineSettingsModal } from './PipelineSettingsModal';
import { CreateDealModal } from './CreateDealModal';
import StatsCard from '../Dashboard/StatsCard';
import SimpleCustomSelect from '../shared/SimpleCustomSelect';
import LoadingScreen from '../shared/LoadingScreen';

function formatCurrency(val: number): string {
  if (val >= 1_000_000) return `$${(val / 1_000_000).toFixed(1)}M`;
  if (val >= 1_000) return `$${(val / 1_000).toFixed(0)}K`;
  return `$${val.toFixed(0)}`;
}

const PipelinePage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canEditStages = canEditPipelineStages(user);
  const tz = useTimezone();
  const { data: allDeals, isLoading, error, updateDeal, createDeal } = usePipelineDeals();
  const { stages, isLoading: stagesLoading, saveStages, DEFAULT_STAGES, refetch: refetchStages } = usePipelineStagesConfig();
  const [searchTerm, setSearchTerm] = useState('');
  const [stageFilter, setStageFilter] = useState('all');
  const [timeFilter, setTimeFilter] = useState('all');
  const [personFilter, setPersonFilter] = useState('all');
  const [currentView, setCurrentView] = useState<'kanban' | 'table'>('kanban');

  const [showSettings, setShowSettings] = useState(false);
  const [draggedDeal, setDraggedDeal] = useState<any>(null);
  const [dragOverStage, setDragOverStage] = useState<string | null>(null);
  const [showCreateDeal, setShowCreateDeal] = useState(false);

  const stageNameToId = useMemo(() => {
    const map: Record<string, string> = {};
    stages.forEach(s => {
      map[s.name.toLowerCase().replace(/\s+/g, '_')] = s.id;
      map[s.name.toLowerCase()] = s.id;
      map[s.id] = s.id;
    });
    return map;
  }, [stages]);

  const resolveStageId = (raw: string): string => {
    const normalized = (raw || '').toLowerCase().replace(/\s+/g, '_');
    return stageNameToId[normalized] || stageNameToId[(raw || '').toLowerCase()] || normalized;
  };

  const kpis = useMemo(() => {
    const closedWonId = stages.find(s => s.closing_probability === 100)?.id;
    const closedLostId = stages.find(s => s.closing_probability === 0 && s.name.toLowerCase().includes('lost'))?.id;

    const closedDeals = allDeals.filter(d => resolveStageId(d.pipeline_stage) === closedWonId).length;

    const activeDeals = allDeals.filter(d => {
      const sid = resolveStageId(d.pipeline_stage);
      return sid !== closedWonId && sid !== closedLostId;
    });

    const pipelineAmount = activeDeals.reduce((sum, d) => sum + (d.deal_value || 0), 0);
    const closedAmount = allDeals
      .filter(d => resolveStageId(d.pipeline_stage) === closedWonId)
      .reduce((sum, d) => sum + (d.deal_value || 0), 0);

    const weightedPipeline = allDeals.reduce((sum, d) => {
      const sid = resolveStageId(d.pipeline_stage);
      const stage = stages.find(s => s.id === sid);
      const prob = stage ? stage.closing_probability / 100 : 0;
      return sum + (d.deal_value || 0) * prob;
    }, 0);

    return { totalDeals: allDeals.length, closedDeals, pipelineAmount, closedAmount, weightedPipeline };
  }, [allDeals, stages]);

  const salesPersons = useMemo(() => {
    const names = new Set<string>();
    allDeals.forEach(d => { if (d.sales_person) names.add(d.sales_person); });
    return Array.from(names).sort();
  }, [allDeals]);

  const existingLeadIds = useMemo(() => allDeals.map(d => d.lead_id), [allDeals]);

  const filteredDeals = useMemo(() => {
    return allDeals.filter(deal => {
      const matchesSearch =
        (deal.company_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (deal.dm_full_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (deal.industry_1 || '').toLowerCase().includes(searchTerm.toLowerCase());

      const dealStageId = resolveStageId(deal.pipeline_stage);
      const matchesStage = stageFilter === 'all' || dealStageId === stageFilter;

      let matchesTime = true;
      if (timeFilter !== 'all') {
        const created = new Date(deal.deal_created_at);
        const now = new Date();
        const createdDate = formatInTz(created, 'yyyy-MM-dd', tz);
        const todayDate = formatInTz(now, 'yyyy-MM-dd', tz);
        if (timeFilter === 'today') matchesTime = createdDate === todayDate;
        else if (timeFilter === 'week') matchesTime = created >= new Date(now.getTime() - 7 * 86400000);
        else if (timeFilter === 'month') {
          const createdMonth = formatInTz(created, 'yyyy-MM', tz);
          const currentMonth = formatInTz(now, 'yyyy-MM', tz);
          matchesTime = createdMonth === currentMonth;
        }
      }

      const matchesPerson = personFilter === 'all' || deal.sales_person === personFilter;

      return matchesSearch && matchesStage && matchesTime && matchesPerson;
    });
  }, [allDeals, searchTerm, stageFilter, timeFilter, personFilter, stages]);

  const getDealsByStage = (stageId: string) => {
    return filteredDeals.filter(d => resolveStageId(d.pipeline_stage) === stageId);
  };

  const handleViewDetails = (deal: any) => { navigate(`/pipeline/${deal.deal_id}`); };

  const handleDragStart = (e: React.DragEvent, deal: any) => {
    setDraggedDeal(deal);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', '');
  };

  const handleDragOver = (e: React.DragEvent, stageId: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverStage(stageId);
  };

  const handleDrop = async (e: React.DragEvent, targetStageId: string) => {
    e.preventDefault();
    setDragOverStage(null);
    if (!draggedDeal) return;

    const currentStageId = resolveStageId(draggedDeal.pipeline_stage);
    if (currentStageId === targetStageId) { setDraggedDeal(null); return; }

    const targetStage = stages.find(s => s.id === targetStageId);
    try {
      await updateDeal(draggedDeal.deal_id, { pipeline_stage: targetStage?.name || targetStageId });
    } catch (err) {
      console.error('[PipelinePage] Failed to update deal stage:', err);
    } finally {
      setDraggedDeal(null);
    }
  };



  const handleSettingsSave = async (newStages: any[]) => {
    const success = await saveStages(newStages);
    if (success) refetchStages();
    return success;
  };

  if ((isLoading || stagesLoading) && allDeals.length === 0) return <LoadingScreen />;

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-50/80 backdrop-blur-sm border border-red-200/60 text-red-700 p-4 rounded-xl flex items-center space-x-3 shadow-sm">
          <AlertCircle size={20} className="flex-shrink-0" />
          <span className="font-medium">{error}</span>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
        <StatsCard title="Total Deals" value={kpis.totalDeals} icon={FileText} color="blue" />
        <StatsCard title="Pipeline Amount" value={formatCurrency(kpis.pipelineAmount)} icon={DollarSign} color="green" />
        <StatsCard title="Weighted Pipeline" value={formatCurrency(kpis.weightedPipeline)} icon={TrendingUp} color="orange" />
        <StatsCard title="Closed Deals" value={kpis.closedDeals} icon={CheckCircle} color="blue" />
        <StatsCard title="Closed Amount" value={formatCurrency(kpis.closedAmount)} icon={DollarSign} color="green" />
      </div>

      <div className="bg-gradient-to-br from-white to-[#1875FF]/5 rounded-xl border-2 border-[#1875FF]/20 shadow-lg p-5">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3 flex-1 min-w-[200px]">
            <button
              onClick={() => setShowCreateDeal(true)}
              className="flex-shrink-0 flex items-center gap-2 px-4 py-2.5 rounded-lg font-bold text-white bg-gradient-to-r from-[#1875FF] to-[#05E0D1] hover:shadow-lg hover:shadow-[#1875FF]/25 transition-all text-sm"
            >
              <Plus size={18} />
              <span className="hidden sm:inline">Create a Deal</span>
            </button>
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Search deals..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-[#1875FF] focus:border-[#1875FF] transition-all font-medium"
              />
            </div>
          </div>

          <div className="flex items-center space-x-3">
            <SimpleCustomSelect
              value={timeFilter}
              onChange={setTimeFilter}
              options={[
                { value: 'all', label: 'All Time' },
                { value: 'today', label: 'Today' },
                { value: 'week', label: 'This Week' },
                { value: 'month', label: 'This Month' }
              ]}
              placeholder="All Time"
              className="w-48"
            />

            <SimpleCustomSelect
              value={stageFilter}
              onChange={setStageFilter}
              options={[
                { value: 'all', label: 'All Stages' },
                ...stages.map(s => ({ value: s.id, label: s.name }))
              ]}
              placeholder="All Stages"
              className="w-56"
            />

            <SimpleCustomSelect
              value={personFilter}
              onChange={setPersonFilter}
              options={[
                { value: 'all', label: 'All Sales People' },
                ...salesPersons.map(name => ({ value: name, label: name }))
              ]}
              placeholder="All Sales People"
              className="w-56"
            />

            {canEditStages && (
              <button
                onClick={() => setShowSettings(true)}
                className="w-9 h-9 flex items-center justify-center rounded-md text-gray-500 hover:text-gray-700 hover:bg-white bg-gray-50 border-2 border-gray-200 transition-all"
                title="Pipeline Settings"
              >
                <Settings size={16} />
              </button>
            )}

            <div className="flex items-center space-x-1 bg-gray-50 rounded-lg p-1 border-2 border-gray-200">
              <button
                onClick={() => setCurrentView('kanban')}
                className={`w-9 h-9 flex items-center justify-center rounded-md transition-all ${
                  currentView === 'kanban'
                    ? 'bg-gradient-to-br from-[#1875FF] to-[#05E0D1] text-white shadow-md'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-white'
                }`}
                title="Kanban View"
              >
                <LayoutGrid size={16} />
              </button>
              <button
                onClick={() => setCurrentView('table')}
                className={`w-9 h-9 flex items-center justify-center rounded-md transition-all ${
                  currentView === 'table'
                    ? 'bg-gradient-to-br from-[#1875FF] to-[#05E0D1] text-white shadow-md'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-white'
                }`}
                title="Table View"
              >
                <Table size={16} />
              </button>
            </div>
          </div>
        </div>
      </div>

      {currentView === 'kanban' ? (
        <div className="overflow-x-auto pipeline-scrollbar">
          <div className="flex space-x-5 min-w-max pb-4">
            {stages.map(stage => {
              const stageDeals = getDealsByStage(stage.id);
              const isDropZone = dragOverStage === stage.id;
              const isDragging = draggedDeal !== null;

              return (
                <div key={stage.id} className="flex-shrink-0 w-80">
                  <div
                    className={`bg-gradient-to-br from-white to-[#1875FF]/5 rounded-xl border-2 shadow-lg flex flex-col max-h-[680px] transition-all duration-300 ease-in-out ${
                      isDropZone
                        ? 'border-[#05E0D1] shadow-[0_0_20px_rgba(5,224,209,0.5)] bg-gradient-to-br from-[#05E0D1]/10 to-[#1875FF]/10'
                        : isDragging
                        ? 'border-[#1875FF]/30 opacity-80'
                        : 'border-[#1875FF]/20 hover:shadow-xl hover:border-[#1875FF]/30'
                    }`}
                    onDragOver={(e) => handleDragOver(e, stage.id)}
                    onDragLeave={() => setDragOverStage(null)}
                    onDrop={(e) => handleDrop(e, stage.id)}
                  >
                    <div className={`p-4 border-b-2 transition-all duration-300 flex-shrink-0 rounded-t-xl ${
                      isDropZone
                        ? 'bg-gradient-to-r from-[#05E0D1]/20 to-[#1875FF]/20 border-[#05E0D1]/30'
                        : 'border-[#1875FF]/10 bg-gradient-to-r from-[#05E0D1]/5 to-[#1875FF]/5'
                    }`}>
                      <div className="flex items-center justify-between mb-2">
                        <h3 className={`font-bold transition-colors duration-300 font-display ${
                          isDropZone ? 'text-[#05E0D1]' : 'text-gray-800'
                        }`}>{stage.name}</h3>
                        <span className={`flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold font-mono border-2 shadow-sm transition-all duration-300 ${
                          isDropZone
                            ? 'bg-gradient-to-br from-[#05E0D1]/30 to-[#1875FF]/30 text-[#05E0D1] border-[#05E0D1]/50'
                            : 'bg-gradient-to-br from-[#1875FF]/20 to-[#05E0D1]/20 text-[#1875FF] border-[#1875FF]/30'
                        }`}>{stageDeals.length}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <p className="text-xs text-gray-600 leading-relaxed font-medium">{stage.description}</p>
                        <span className="text-[10px] font-semibold text-gray-400 ml-2 whitespace-nowrap">{stage.closing_probability}%</span>
                      </div>
                    </div>

                    <div className={`p-3 space-y-2.5 transition-all duration-300 overflow-y-auto flex-1 min-h-0 pipeline-stage-scroll ${isDropZone ? 'bg-[#05E0D1]/5' : ''}`}>
                      {stageDeals.map(deal => (
                        <div
                          key={deal.deal_id}
                          className="transition-all duration-300 ease-in-out"
                          onDragEnd={() => { setDraggedDeal(null); setDragOverStage(null); }}
                        >
                          <LeadCard
                            lead={deal}
                            onClick={() => handleViewDetails(deal)}
                            onDragStart={handleDragStart}
                          />
                        </div>
                      ))}
                      {stageDeals.length === 0 && (
                        <div className={`text-center py-20 transition-all duration-300 ${isDropZone ? 'text-[#05E0D1]' : 'text-gray-400'}`}>
                          <div className={`text-sm font-medium ${isDropZone ? 'animate-pulse' : ''}`}>
                            {isDropZone ? 'Drop here' : 'No deals in this stage'}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <PipelineTable leads={filteredDeals} onViewDetails={handleViewDetails} stages={stages} />
      )}

      <PipelineSettingsModal
        isOpen={showSettings}
        onClose={() => setShowSettings(false)}
        stages={stages}
        defaults={DEFAULT_STAGES}
        onSave={handleSettingsSave}
      />

      {showCreateDeal && (
        <CreateDealModal
          stages={stages}
          existingLeadIds={existingLeadIds}
          salesPersons={salesPersons}
          currentUserName={user?.name || ''}
          onCreateDeal={createDeal}
          onClose={() => setShowCreateDeal(false)}
        />
      )}
    </div>
  );
};

export default PipelinePage;
